import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { getApiBase, isNative } from '@/lib/api-base';
import { isNativeGoogleAuthAvailable, signInWithGoogleNative } from '@/lib/native-google-auth';
import { useAuthStore } from '@/store/authStore';
import { useGoogleAuthEnabled } from '@/api/queries';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

/**
 * Official Google "G" mark.
 *
 * Per Google's Sign-In branding guidelines the mark must be used as-is: the
 * four brand colours are hard-coded and must NOT be recoloured, tinted or
 * themed, so these fills are intentionally literal instead of CSS variables.
 */
function GoogleMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8409H9v3.4814h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9086c1.7018-1.5668 2.6837-3.874 2.6837-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9086-2.2581c-.8059.54-1.8368.859-3.0478.859-2.344 0-4.3282-1.5831-5.036-3.7104H.9574v2.3318C2.4382 15.9832 5.4818 18 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71c-.18-.54-.2822-1.1168-.2822-1.71s.1023-1.17.2823-1.71V4.9582H.9573A8.9965 8.9965 0 0 0 0 9c0 1.4523.3477 2.8268.9573 4.0418L3.964 10.71z"
      />
      <path
        fill="#EA4335"
        d="M9 3.5795c1.3214 0 2.5077.4541 3.4405 1.346l2.5813-2.5814C13.4632.8918 11.426 0 9 0 5.4818 0 2.4382 2.0168.9573 4.9582L3.964 7.29C4.6718 5.1627 6.656 3.5795 9 3.5795z"
      />
    </svg>
  );
}

interface GoogleSignInButtonProps {
  /** Where to land after a successful sign-in (defaults to /dashboard). */
  redirectTo?: string;
  /** Referral code captured from a ?ref= link, forwarded to the signup. */
  referralCode?: string | null;
  /** Disable while the sibling e-mail form is submitting. */
  disabled?: boolean;
}

/**
 * "Continuar com Google" CTA.
 *
 * Renders NOTHING when the backend reports the feature as unconfigured (the
 * production case today) or while the status is still loading, so there is
 * never a button that leads to a 501. On the Capacitor shell it swaps to the
 * native account picker instead of the browser redirect (Google blocks OAuth
 * inside an embedded WebView outright), and hides itself if the native
 * client id isn't configured for the build.
 */
export function GoogleSignInButton({ redirectTo, referralCode, disabled }: GoogleSignInButtonProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useGoogleAuthEnabled();
  const loginWithGoogleIdToken = useAuthStore((s) => s.loginWithGoogleIdToken);
  const googleErrorMessage = useGoogleAuthErrorMessage();
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);
  const [nativeError, setNativeError] = useState('');

  if (isLoading || !data?.data?.enabled) return null;
  if (isNative() && !isNativeGoogleAuthAvailable()) return null;

  const startWeb = () => {
    setRedirecting(true);
    const params = new URLSearchParams();
    if (redirectTo) params.set('redirect', redirectTo);
    if (referralCode) params.set('ref', referralCode);
    const qs = params.toString();
    // Full-page navigation — the OAuth consent screen refuses to be framed.
    window.location.href = `${getApiBase()}/auth/google${qs ? `?${qs}` : ''}`;
  };

  const startNative = async () => {
    setNativeError('');
    setRedirecting(true);
    try {
      const idToken = await signInWithGoogleNative();
      const { isNewUser } = await loginWithGoogleIdToken(idToken, referralCode);
      if (redirectTo) {
        navigate({ to: redirectTo, replace: true });
      } else if (isNewUser) {
        navigate({ to: '/dashboard', search: { onboarding: 'true' }, replace: true });
      } else {
        navigate({ to: '/dashboard', replace: true });
      }
    } catch (err) {
      const code = (err as { code?: string })?.code;
      // The user closing the account picker isn't an error worth a banner.
      if (code !== 'USER_CANCELLED') {
        setNativeError(googleErrorMessage(code === 'disabled' ? 'disabled' : 'provider_error'));
      }
      setRedirecting(false);
    }
  };

  const start = isNative() ? startNative : startWeb;

  return (
    <div>
      <button
        type="button"
        onClick={start}
        disabled={disabled || redirecting}
        // Google's brand guideline button is fixed white/dark-gray — it must
        // NEVER follow our app's theme. index.css has an UNLAYERED
        // `button, select { color: inherit }` rule (needed elsewhere, to
        // stop buttons defaulting to system ButtonText); unlayered CSS beats
        // ANY @layer'd rule regardless of specificity, so it was silently
        // winning over Tailwind's `text-[#3c4043]` utility (layered) and
        // making this button's text follow the page's theme color — white
        // on white in dark mode. An inline style outranks both.
        style={{ color: '#3c4043' }}
        className="w-full h-11 inline-flex items-center justify-center gap-3 rounded-xl bg-white font-medium text-sm border border-[#dadce0] transition-shadow hover:shadow-[0_1px_3px_rgba(60,64,67,0.3)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4285F4] disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {redirecting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <GoogleMark className="h-[18px] w-[18px]" />
        )}
        {redirecting ? t('auth:google.redirecting') : t('auth:google.continueWith')}
      </button>

      {nativeError && (
        <p className="mt-2 text-xs text-[var(--color-destructive)]">{nativeError}</p>
      )}

      {/* Divider between the provider CTA and the e-mail form below */}
      <div className="flex items-center gap-3 my-6">
        <span className="h-px flex-1 bg-[var(--color-border)]" />
        <span className="text-[10px] uppercase tracking-widest text-[var(--color-text-muted)]">
          {t('auth:google.or')}
        </span>
        <span className="h-px flex-1 bg-[var(--color-border)]" />
      </div>
    </div>
  );
}

/** Maps the API's stable error codes to a translated message. */
export function useGoogleAuthErrorMessage() {
  const { t } = useTranslation();
  return (code: string | undefined): string => {
    switch (code) {
      case 'cancelled':
        return t('auth:google.errors.cancelled');
      case 'email_unverified':
        return t('auth:google.errors.emailUnverified');
      case 'account_conflict':
        return t('auth:google.errors.accountConflict');
      case 'invalid_state':
        return t('auth:google.errors.invalidState');
      case 'disabled':
        return t('auth:google.errors.disabled');
      case 'rate_limited':
        return t('auth:google.errors.rateLimited');
      default:
        return t('auth:google.errors.providerFailure');
    }
  };
}
